import posthog from "posthog-js";
import { apiFetch } from "../../../shared/api-client";

/**
 * How each Company uses what it signs into — PostHog, in the same project
 * as every Company's public form (web/src/lib/analytics.ts). The two are
 * told apart by `app`, and both are read Company by Company by `company`:
 * the slug GET /api/admin/me names, which is the label the Company's own
 * form events carry, so one Company's form and its use of admin read as
 * one account.
 *
 * The people here are Company Users, identified by their uid. What they
 * read is the Company's Customers — names, numbers, what was said on a
 * call — so no text reaches PostHog: autocapture keeps a click's element
 * and drops its words, and a recording masks every input and every line
 * of text. What a click meant, where the element alone cannot say, is
 * named by the events below.
 *
 * Inert unless the build injected a key, which tests and `bun run dev`
 * never do.
 */

declare const __POSTHOG_KEY__: string | undefined;

let enabled = false;

export function initAnalytics(): void {
  if (typeof __POSTHOG_KEY__ === "undefined" || !__POSTHOG_KEY__) return;
  posthog.init(__POSTHOG_KEY__, {
    api_host: "https://us.i.posthog.com",
    autocapture: true,
    mask_all_text: true,
    // A single-page app: a page is a route change, not a document load.
    capture_pageview: "history_change",
    capture_pageleave: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
    },
    respect_dnt: true,
  });
  posthog.register({ app: "admin" });
  enabled = true;
}

/**
 * Name the signed-in person and their Company on everything from here on.
 * Asks the API which Company that is, so it runs only where analytics is
 * on; a failure leaves the events unlabelled rather than the page broken.
 */
export async function identifyAnalyticsUser(
  uid: string,
  email: string | null,
  signal?: AbortSignal,
): Promise<void> {
  if (!enabled) return;
  try {
    const me = await apiFetch<{ company: { slug: string } }>("/api/admin/me", {
      signal,
    });
    posthog.identify(uid, { email, company: me.company.slug });
    posthog.register({ company: me.company.slug });
  } catch {
    // Unlabelled events are still events; nothing here is worth an error.
  }
}

/** Forget the person on sign-out, so the next one on this browser is theirs. */
export function resetAnalytics(): void {
  if (!enabled) return;
  posthog.reset();
  posthog.register({ app: "admin" });
}

/**
 * `change_saved` is every committed edit, from the one save hook every
 * setting and every field on a call goes through; the page it was on says
 * which. `summary_edited` is the one of those worth counting on its own:
 * a Call Summary somebody corrected is the model's reading of a call
 * being wrong.
 */
export type AnalyticsEvent =
  | "change_saved"
  | "summary_edited"
  | "recording_played";

export function track(event: AnalyticsEvent): void {
  if (!enabled) return;
  posthog.capture(event);
}
